import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";

import { CheckpointDetailCard } from "@/presentation/components/timeline-v3/checkpoint-detail-card";
import { HorizontalTrack } from "@/presentation/components/timeline-v3/horizontal-track";
import type { MilestoneKey } from "@/presentation/components/timeline-v3/horizontal-track";
import { PageExplainer } from "@/presentation/components/shell/page-explainer";
import { PageHeader } from "@/presentation/components/shell/page-header";
import { WeekDetailCard } from "@/presentation/components/timeline-v3/week-detail-card";

import { getCurrentWeekNumber, timelineV3 } from "./-timeline-v3.data";
import type { CheckpointEntry, TimelineEntry, WeekEntry } from "./-timeline-v3.data";

export const Route = createFileRoute("/_app/timeline-v3")({
  component: TimelineV3Page,
});

function TimelineV3Page() {
  const currentWeek = getCurrentWeekNumber();
  const [selected, setSelected] = useState<MilestoneKey>(currentWeek ?? 1);
  return (
    <div className="animate-page-enter mx-auto flex w-full max-w-5xl flex-col">
      <div className="flex justify-end px-6 pt-6 lg:px-10 print:hidden">
        <PageExplainer pageKey="schedule" />
      </div>
      <PageHeader
        eyebrow="Extended runway · candidate-selection lens"
        title="Timeline 3.0"
        summary="The 4-week May discovery + build-spec phase extended by two weeks of market research and architecture-candidate evaluation. The Jun-14 checkpoint locks the candidate Yango will commit to under the top-weighted criteria. Click a node to view its detail."
      />
      <PageBody
        entries={timelineV3}
        selected={selected}
        currentWeek={currentWeek}
        onSelect={setSelected}
      />
    </div>
  );
}

interface PageBodyProps {
  entries: TimelineEntry[];
  selected: MilestoneKey;
  currentWeek: 1 | 2 | 3 | 4 | 5 | 6 | null;
  onSelect: (key: MilestoneKey) => void;
}

function PageBody({ entries, selected, currentWeek, onSelect }: PageBodyProps) {
  const detailEntry = findEntry(entries, selected);
  return (
    <div className="flex flex-col gap-6 px-6 py-8 lg:px-12">
      <div className="print:hidden">
        <HorizontalTrack
          entries={entries}
          selected={selected}
          currentWeek={currentWeek}
          onSelect={onSelect}
        />
      </div>
      <Detail entry={detailEntry} />
    </div>
  );
}

function Detail({ entry }: { entry: TimelineEntry | null }) {
  if (!entry) return null;
  if (entry.kind === "checkpoint") {
    return <CheckpointDetailCard entry={entry} />;
  }
  return <WeekDetailCard key={entry.number} entry={entry} />;
}

function findEntry(entries: TimelineEntry[], key: MilestoneKey): TimelineEntry | null {
  if (typeof key === "string") {
    return (
      (entries.find((e) => e.kind === "checkpoint" && e.id === key) as
        | CheckpointEntry
        | undefined) ?? null
    );
  }
  return (
    (entries.find((e) => e.kind === "week" && e.number === key) as WeekEntry | undefined) ?? null
  );
}
